import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import UserQueryKeyGenerator from './UserQueryKeyGenerator'

import { getCurrentUser } from '@/api/query'
import { consumeUserDataCookie } from '@/lib/userDataCookie'
import { JsonResponse } from '@/types'
import { User } from '@/types/user.type'

export const useGetCurrentUser = () => {
  // Read and consume user data from cookie (set by middleware)
  // This is memoized to only run once on initial render
  const initialUserData = useMemo(() => {
    const userData = consumeUserDataCookie()
    if (userData) {
      // Return data in the same format as getCurrentUser response
      return {
        data: {
          response: userData,
        } as JsonResponse<User>,
      }
    }
    return undefined
  }, [])

  return useQuery({
    queryKey: UserQueryKeyGenerator.currentUser(),
    queryFn: getCurrentUser,
    retry: (failureCount, error: any) => {
      // Don't retry on 401 errors (authentication failure)
      if (error?.response?.status === 401) {
        return false
      }
      // Retry other errors up to 3 times
      return failureCount < 3
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Use cookie data as initial data if available
    // This prevents a duplicate /users/me call since middleware already fetched the data
    initialData: initialUserData,
    // Tell React Query that the initialData was just fetched (now)
    // This ensures it respects the staleTime and doesn't immediately refetch
    initialDataUpdatedAt: initialUserData ? Date.now() : undefined,
  })
}
